import { Injectable, HostListener } from "@angular/core";
import { oneline } from "../utilities";

// :(
// Some reason TS doesn't recognize these defs even though from its own lib!
interface AudioScheduledSourceNode extends AudioNode
{
  start : () => void;
  stop : () => void;
  onended : (ev : Event) => void;
}
interface MediaElementAudioSourceNode_PATCH extends AudioNode {
  readonly mediaElement : HTMLMediaElement;
}

export type SoundEffect = "chat" |  "arrive";

@Injectable()
export class SoundsService
{
    private context : AudioContext;
    private masterGain : GainNode;
    private volume : number = 0.3;
    public readonly sampleRate : number = 44100;
    public setVolume (v : number) : void
    {
      if (v >= 0 && v <= 1)
      {
        this.volume = v;
        this.masterGain.gain.value = v;
      }
    }

    public static readonly PreloadMedia : Map<SoundEffect, string> = new Map([
      ["chat", "/static/sound/two-tone.ogg"],
      ["arrive", "/static/sound/doorbell.ogg"]
    ] as Array<[SoundEffect, string]>);
    public audioFileNodes :
      Map<SoundEffect, MediaElementAudioSourceNode_PATCH> =
      new Map();

    private scale : Scale = new Scale();
    private activeNodes : AudioScheduledSourceNode[] = [];
    private karplus : KarplusStrongSynth;

    private killNode (node : AudioScheduledSourceNode) : void
    {
      node.stop();
      node.disconnect();
      const i : number = this.activeNodes.indexOf(node);
      if (i > -1)
      {
        this.activeNodes.splice(i, 1);
      }
    }

    private killAll () : void
    {
      this.activeNodes.forEach(
        (n : AudioScheduledSourceNode) => {
          n.stop();
          n.disconnect();
        }
      );
      this.activeNodes = [];
    }

    constructor ()
    {
        this.context = new AudioContext();
        this.masterGain = this.context.createGain();
        this.masterGain.gain.value = this.volume;
        this.masterGain.connect(this.context.destination);
        this.karplus = new KarplusStrongSynth(this.context);
        setTimeout(() => this.attachSounds_HACK(), 500);
        this.unlockListener = () => {this.forceAPIUnlock();};
        document.addEventListener("click", this.unlockListener);
    }

    private unlockListener : () => void;
    private forceAPIUnlock () : void
    {
      // const buffer : AudioBuffer =
      //   this.context.createBuffer(1, 1, this.context.sampleRate);
      // const source : AudioBufferSourceNode = this.context.createBufferSource();
      // source.buffer = buffer;
      // source.connect(this.masterGain);
      // source.start();
      this.context.resume().then(() => {
        console.log("Audio context state:", this.context.state);
      });
      document.removeEventListener("click", this.unlockListener);
    }

    private attachSounds_HACK () : void
    {
      SoundsService.PreloadMedia.forEach(
        (_ : string, id : SoundEffect) => {
          const e : HTMLAudioElement =
            document.getElementById(id) as HTMLAudioElement;
          if (!e)
          {
            throw Error(`Could not find audio element #${id}`);
          }
          const node : MediaElementAudioSourceNode_PATCH =
            <MediaElementAudioSourceNode_PATCH>
              this.context.createMediaElementSource(e);
          node.mediaElement.load();
          node.connect(this.masterGain);
          this.audioFileNodes.set(id, node);
        }
      );
    }

    private hasSound (name : string) : name is SoundEffect
    {
      return this.audioFileNodes.has(name as SoundEffect);
    }

    public playSound (name : SoundEffect) : void
    public playSound (name : string) : void
    {
      if (this.hasSound(name))
      {
        this.audioFileNodes.get(name).mediaElement.play();
        console.warn(oneline`
          attempting to play sound ${name}
          (${this.audioFileNodes.get(name).mediaElement.src})`);
      }
    }

    public playKarplus (note : string)
    {
      const f : number = this.scale.frequency(note);
      const node : AudioNode = this.karplus.create(f);
    }

    public attachAndPlay (node : AudioScheduledSourceNode)
    {
      node.connect(this.masterGain);
      this.activeNodes.push(node);
      node.start();
      node.onended = () => this.killNode(node);
    }

    public quickPlay (
      note : string,
      time : number = 0,
      shape : "sine" | "square" | "sawtooth" | "triangle" = "sine")
    {
      const node : OscillatorNode = this.context.createOscillator();
      node.type = shape;
      node.frequency.value = this.scale.frequency(note);
      this.attachAndPlay(node);
      if (time > 0)
      {
        // use better audio timeout techniques?
        setTimeout(() => this.killNode(node), time);
      }
    }

    public debugTest () : void
    {
      this.quickPlay("C4", 2000, "sawtooth");
      this.quickPlay("E4", 2000, "sawtooth");
      this.quickPlay("G4", 2000, "sawtooth");
    }
}

enum Tuning { EqualTemprament }
const EqualTempramentStepSize : number = Math.pow(2, 1 / 12);

class Scale
{
    private defaultOctave : number = 4;

    public static readonly KeyLetters : Map<string, number> = new Map([
      ["c", 0],
      ["d", 2],
      ["e", 4],
      ["f", 5],
      ["g", 7],
      ["a", 9],
      ["b", 11]
    ]);
    public static readonly AccentCharacters : Map<string, number> = new Map([
      ["♭", -1],
      ["b", -1],
      ["𝄫", -2],
      ["-", -1],
      ["♯", 1],
      ["#", 1],
      ["𝄪", 2],
      ["+", 1],
      ["♮", 0]
    ]);

    constructor (
        public aFrequency : number = 440,
        public tuning : Tuning = Tuning.EqualTemprament)
    {
      /* */
    }

    private nameToMidiNumber (noteName : string) : number
    {
      if (noteName.length === 0 || noteName.length > 3)
      {
        throw Error(`Note '${noteName}' was not between 1-3 characters`);
      }
      let octaveRelative : number =
        Scale.KeyLetters.get(noteName[0].toLowerCase());
      if (Scale.AccentCharacters.has(noteName[1]))
      {
        octaveRelative += Scale.AccentCharacters.get(noteName[1]);
      }
      let octave : number = Number.parseInt(noteName.slice(-1), 10);
      if (!Number.isSafeInteger(octave))
      {
        octave = this.defaultOctave;
      }
      return ((octave + 1) * 12) + octaveRelative;
    }

    private midiNumberToFrequency (midiNumber : number) : number
    {
      if (this.tuning !== Tuning.EqualTemprament)
      {
        throw Error("Non equal-tempered tunings are not supported!");
      }
      const octaves : number = Math.trunc((midiNumber - 69) / 12);
      const steps : number = ((midiNumber - 69) % 12) + 12;
      return (
        Math.pow(2, octaves)
        * Math.pow(EqualTempramentStepSize, steps)
        * this.aFrequency);
    }

    public frequency (noteName : string) : number
    {
      return this.midiNumberToFrequency(this.nameToMidiNumber(noteName));
    }
}

class KarplusStrongSynth
{
    constructor (
      private context : AudioContext,
      private fadeRate : number = 0.9)
    {
      /* */
    }

    public create (frequency : number) : AudioScheduledSourceNode
    {
      const length : number = Math.floor(this.context.sampleRate / frequency);
      const buffer : AudioBuffer =
        this.context.createBuffer(1, length, this.context.sampleRate);
      const data : Float32Array = buffer.getChannelData(0);
      for (let i : number = 0; i < length; i++)
      {
        data[i] = Math.random() * 2 - 1;
      }
      const node : AudioBufferSourceNode = this.context.createBufferSource();
      node.buffer = buffer;
      return node;
    }
}

function makeDistortionCurve (amount : number = 20) : Float32Array
{
  const sampleCount : number = 256;
  const curve : Float32Array = new Float32Array(sampleCount);
  for (let i : number = 0 ; i < sampleCount; ++i ) {
      const x : number = i * 2 / sampleCount - 1;
      curve[i] = (Math.PI + amount) * x / (Math.PI + amount * Math.abs(x));
  }
  return curve;
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/support/APIs/sounds.service.ts